import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useCalculatorTheme } from "@/contexts/CalculatorThemeContext";

const calculatorButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

export interface CalculatorButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof calculatorButtonVariants> {
  asChild?: boolean;
  variant?: "default" | "outline" | "ghost" | "primary" | "success" | "danger";
}

const CalculatorButton = React.forwardRef<HTMLButtonElement, CalculatorButtonProps>(
  ({ className, variant = "default", size, asChild = false, ...props }, ref) => {
    // Try to use theme context, fallback to dark if not available
    let isDark = true;
    try {
      const theme = useCalculatorTheme();
      isDark = theme.isDark;
    } catch {
      // Component is used outside of CalculatorThemeProvider, default to dark
    }

    const getVariantStyles = () => {
      if (isDark) {
        switch (variant) {
          case "outline":
            return "border border-amber-400/30 bg-white/5 text-amber-300 hover:bg-amber-400/10 hover:text-amber-200 hover:border-amber-400/50";
          case "ghost":
            return "text-white/70 hover:bg-white/10 hover:text-white";
          case "primary":
            return "bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20";
          case "success":
            return "bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/30";
          case "danger":
            return "bg-red-500/20 border border-red-400/30 text-red-300 hover:bg-red-400/30";
          default:
            return "bg-white/10 border border-white/20 text-white hover:bg-white/20";
        }
      } else {
        switch (variant) {
          case "outline":
            return "border border-slate-300 bg-white/80 text-slate-700 hover:bg-slate-100 hover:border-slate-400";
          case "ghost":
            return "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
          case "primary":
            return "bg-gradient-to-r from-navy to-navy-light text-white hover:from-navy-light hover:to-navy shadow-lg shadow-navy/20";
          case "success":
            return "bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 hover:bg-emerald-500/20";
          case "danger":
            return "bg-red-500/10 border border-red-500/30 text-red-700 hover:bg-red-500/20";
          default:
            return "bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200";
        }
      }
    };

    const Comp = asChild ? Slot : "button";
    
    return (
      <Comp 
        className={cn(
          calculatorButtonVariants({ size }),
          getVariantStyles(),
          className
        )} 
        ref={ref} 
        {...props} 
      />
    );
  },
);
CalculatorButton.displayName = "CalculatorButton";

export { CalculatorButton, calculatorButtonVariants };
